// InformationRequestPendingCard — the DISTINCT "a form is being prepared" load
// state (F53).
//
// When the agent starts emitting a structured elicitation form, the vendor names
// the `_request_information` tool_use block seconds into generation, long before
// the form's fields finish streaming (surfaced as the transient
// `information_request_pending` turn-stream signal → `InformationRequestPendingEntry`).
// Until F53 that whole window rendered as the GENERIC working indicator
// ("Skissar… 0:15"), which reads as the agent having FROZEN — no signal a FORM is
// coming. This card is the fix: it wears the SAME visual language as the real
// `InformationRequestCard` (the same `.bilda-info-request` ground + field layout)
// so it reads as "the form, loading" rather than a new kind of thing, with a
// working spark + "Preparing form…" and a skeleton of the fields to come.
//
// It is SUPERSEDED by the real form the instant it lands (the turn-stream fold
// swaps the placeholder for the `InformationRequestEntry`, same id), and swept at
// turn end if the form never arrives — so this never outlives its turn.
//
// D30: as the agent writes the form, each finished field adds a PROGRESS STEP
// (`parts`: its agent-authored purpose, e.g. "Förbereder frågor om målgrupp", in
// the conversation's language), listed done-ticked above one skeleton field for
// what is still coming. The steps are data streamed from the server, so the
// reveal is driven by arrival, never by a timer.
//
// Pure/props-driven — it renders only the parts it is given and holds no state.
// alarm-discipline: the "preparing" state is a QUIET status (role=status, one
// polite announcement), never an alarm; the skeleton tiles are decorative
// (`aria-hidden`, via Skeleton) so AT hears the state once, not per tile. No
// borders of its own (it inherits the card ground); tokens only.

import { Trans } from "@lingui/react/macro";
import { Icon } from "../../Icon/Icon.js";
import { Skeleton } from "../../Skeleton/Skeleton.js";
import { Text } from "../../Text/Text.js";
import "./InformationRequestCard.css";
import "./InformationRequestPendingCard.css";

export type InformationRequestPendingCardProps = {
    // Progress steps written so far, oldest first. Empty → skeleton only.
    readonly parts?: readonly string[];
    readonly className?: string;
};

// A skeleton of one field: a short label line above a taller control block —
// the shape a real `field_*` renders into, so the placeholder foreshadows the
// form rather than a generic spinner. Decorative (Skeleton is `aria-hidden`).
const SkeletonField = ({ labelWidth }: { readonly labelWidth: string }) => (
    <li className="bilda-info-request__field">
        <Skeleton variant="text" width={labelWidth} />
        <Skeleton variant="block" height="var(--space-8)" />
    </li>
);

export const InformationRequestPendingCard = ({
    parts = [],
    className,
}: InformationRequestPendingCardProps) => (
    // Same ground + labelled-region shape as the real form, so it reads as the
    // form loading. role=status announces "preparing a form" ONCE (polite).
    <section
        className={`bilda-info-request bilda-info-request-pending${
            className ? ` ${className}` : ""
        }`}
        role="status"
    >
        <header className="bilda-info-request__head">
            <span className="bilda-info-request-pending__head">
                {/* The agent-coloured working spark — generation reads by motion,
                    not colour alone; the label + role=status carry the meaning for
                    AT and under reduced motion (pulse neutralised in tokens.css). */}
                <span
                    className="bilda-info-request-pending__spark"
                    aria-hidden="true"
                />
                <Text as="span" size="sm" weight="medium" tone="dim">
                    <Trans>Preparing form…</Trans>
                </Text>
            </span>
        </header>
        {parts.length > 0 && (
            <ul className="bilda-info-request-pending__parts">
                {parts.map((part, i) => (
                    <li
                        // Parts only ever append; position is their identity.
                        // biome-ignore lint/suspicious/noArrayIndexKey: append-only list
                        key={i}
                        className="bilda-info-request-pending__part"
                    >
                        <Icon name="check" size="xs" />
                        <Text as="span" size="sm" tone="dim">
                            {part}
                        </Text>
                    </li>
                ))}
            </ul>
        )}
        <ol className="bilda-info-request__fields">
            <SkeletonField labelWidth="40%" />
            {parts.length === 0 && <SkeletonField labelWidth="55%" />}
        </ol>
    </section>
);
